import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  FileText,
  Clock,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Shield,
  Star,
  Users,
  Award,
  Phone,
} from "lucide-react";
import { serviceContentData } from "@/data/serviceContentData";
import ServiceInquiryForm from "@/components/ServiceInquiryForm";
import PaymentCheckoutModal from "@/components/PaymentCheckoutModal";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://finease-crm.vercel.app";

function toSlug(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatCrmPrice(priceValue, currencyCode) {
  const amount = Number(priceValue || 0);
  const hasDecimals = !Number.isInteger(amount);
  const formatted = amount.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
  const code = String(currencyCode || "INR").toUpperCase();

  if (code === "USD") return `$${formatted}`;
  if (code === "INR") return `₹${formatted}`;
  return `${code} ${formatted}`;
}

// Helper components to avoid deeply nested member expressions in main component
function TrustSignals({ signals }) {
  if (!signals) return null;
  const incorporations = signals.incorporations || "";
  const rating = signals.rating || "";
  const experience = signals.experience || "";
  const support = signals.support || "";

  return (
    <div className="flex flex-wrap gap-6 mb-8">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-[#00C896]" />
        <span className="text-sm text-slate-300">
          <strong className="text-white">{incorporations}</strong> Clients
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Star className="w-5 h-5 text-[#00C896]" />
        <span className="text-sm text-slate-300">
          <strong className="text-white">{rating}</strong> Rating
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Award className="w-5 h-5 text-[#00C896]" />
        <span className="text-sm text-slate-300">
          <strong className="text-white">{experience}</strong> Experience
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-[#00C896]" />
        <span className="text-sm text-slate-300">{support}</span>
      </div>
    </div>
  );
}

function WhoNeedsSection({ data }) {
  if (!data) return null;
  const title = data.title || "";
  const description = data.description || "";
  const points = data.points || [];

  return (
    <section className="border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {title}
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-3xl">{description}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {points.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5"
              >
                <CheckCircle className="w-5 h-5 text-[#00C896] shrink-0 mt-0.5" />
                <span className="text-slate-300">{point}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function WhyRequiredSection({ data }) {
  if (!data) return null;
  const title = data.title || "";
  const description = data.description || "";
  const points = data.points || [];

  return (
    <section className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {title}
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-3xl">{description}</p>
          <div className="space-y-4">
            {points.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="p-5 rounded-xl bg-[#0A0A0A] border border-white/5"
              >
                <p className="text-slate-300 leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BenefitsSection({ benefits }) {
  if (!benefits || benefits.length === 0) return null;

  return (
    <section className="border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Why Choose This
          </span>
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Key Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const benefitTitle = benefit.title || "";
              const benefitDesc = benefit.description || "";
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#00C896]/20 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#00C896]/10 border border-[#00C896]/20 flex items-center justify-center mb-4">
                    <CheckCircle className="w-5 h-5 text-[#00C896]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefitTitle}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{benefitDesc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function DocumentsSection({ data }) {
  if (!data) return null;
  const title = data.title || "Documents Required";
  const description = data.description || "";
  const directors = data.directors || [];
  const company = data.company || [];

  return (
    <section className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Preparation
          </span>
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {title}
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-3xl">{description}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="p-6 rounded-2xl bg-[#0A0A0A] border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-[#00C896]" />
                <h3 className="text-xl font-semibold text-white">Personal Documents</h3>
              </div>
              <ul className="space-y-3">
                {directors.map((doc, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#00C896] shrink-0 mt-1" />
                    <span className="text-slate-300 text-sm">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 rounded-2xl bg-[#0A0A0A] border border-white/5">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-[#00C896]" />
                <h3 className="text-xl font-semibold text-white">Business Documents</h3>
              </div>
              <ul className="space-y-3">
                {company.map((doc, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#00C896] shrink-0 mt-1" />
                    <span className="text-slate-300 text-sm">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ProcessSection({ steps }) {
  if (!steps || steps.length === 0) return null;

  return (
    <section className="border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            How It Works
          </span>
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Step-by-Step Process
          </h2>
          <div className="space-y-6">
            {steps.map((step, index) => {
              const stepNum = step.step || index + 1;
              const stepTitle = step.title || "";
              const stepDuration = step.duration || "";
              const stepDesc = step.description || "";
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative flex gap-6 p-6 rounded-2xl bg-white/[0.02] border border-white/5"
                >
                  <div className="shrink-0">
                    <div className="w-12 h-12 rounded-full bg-[#00C896]/10 border border-[#00C896]/30 flex items-center justify-center">
                      <span className="text-lg font-bold text-[#00C896]">{stepNum}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-xl font-semibold text-white mb-2">{stepTitle}</h3>
                      <span className="shrink-0 flex items-center gap-1 text-xs font-mono text-[#00C896] bg-[#00C896]/10 px-3 py-1 rounded-full">
                        <Clock className="w-3 h-3" />
                        {stepDuration}
                      </span>
                    </div>
                    <p className="text-slate-400 leading-relaxed">{stepDesc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TimelineSection({ data }) {
  if (!data) return null;
  const total = data.total || "";
  const note = data.note || "";
  const breakdown = data.breakdown || [];

  return (
    <section className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
                Expected Duration
              </span>
              <h2
                className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Timeline
              </h2>
              <div className="inline-block bg-[#00C896]/10 border border-[#00C896]/20 rounded-2xl px-8 py-6 mb-6">
                <p className="text-4xl font-bold text-[#00C896] mb-1">{total}</p>
                <p className="text-sm text-slate-400">Total Duration</p>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">{note}</p>
            </div>
            <div className="space-y-4">
              {breakdown.map((phase, index) => {
                const phaseName = phase.phase || "";
                const phaseDuration = phase.duration || "";
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 rounded-xl bg-[#0A0A0A] border border-white/5"
                  >
                    <span className="text-slate-300">{phaseName}</span>
                    <span className="text-sm font-mono text-[#00C896]">{phaseDuration}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PricingSection({ data, serviceName, serviceSlug }) {
  const [selectedPackage, setSelectedPackage] = useState(null);
  if (!data) return null;
  const title = data.title || "Pricing";
  const description = data.description || "";
  const packages = data.packages || [];
  const governmentFees = data.governmentFees || "";

  return (
    <section className="border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
              Investment
            </span>
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {title}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">{description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {packages.map((pkg, index) => {
              const pkgName = pkg.name || "";
              const pkgPrice = pkg.price || "";
              const pkgDesc = pkg.description || "";
              const pkgFeatures = pkg.features || [];
              const pkgPopular = pkg.popular || false;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={"relative p-6 rounded-2xl border " + (
                    pkgPopular
                      ? "bg-[#00C896]/5 border-[#00C896]/30"
                      : "bg-white/[0.02] border-white/5"
                  )}
                >
                  {pkgPopular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00C896] text-black text-xs font-bold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-xl font-semibold text-white mb-2">{pkgName}</h3>
                  <p className="text-3xl font-bold text-[#00C896] mb-2">{pkgPrice}</p>
                  <p className="text-sm text-slate-400 mb-6">{pkgDesc}</p>
                  <ul className="space-y-3 mb-6">
                    {pkgFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[#00C896] shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={"block w-full text-center py-3 rounded-full font-medium transition-colors " + (
                      pkgPopular
                        ? "bg-[#00C896] text-black hover:bg-[#00E6AC]"
                        : "border border-white/20 text-white hover:bg-white/5"
                    )}
                  >
                    Get Started
                  </Link>
                  <button
                    type="button"
                    onClick={() => setSelectedPackage(pkg)}
                    className="mt-3 block w-full text-center py-3 rounded-full font-medium border border-[#00C896]/40 text-[#00C896] hover:bg-[#00C896]/10 transition-colors"
                  >
                    Pay Now
                  </button>
                </motion.div>
              );
            })}
          </div>
          <p className="text-center text-sm text-slate-500">{governmentFees}</p>
        </motion.div>
      </div>

      <PaymentCheckoutModal
        isOpen={!!selectedPackage}
        onClose={() => setSelectedPackage(null)}
        serviceName={serviceName}
        serviceSlug={serviceSlug}
        packageName={selectedPackage?.name || ""}
        packagePrice={selectedPackage?.price || ""}
      />
    </section>
  );
}

function FAQSection({ faqs, openFaq, toggleFaq }) {
  if (!faqs || faqs.length === 0) return null;

  return (
    <section className="border-t border-white/5">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
              Got Questions?
            </span>
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const question = faq.question || "";
              const answer = faq.answer || "";
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                  className="rounded-xl border border-white/5 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-[#0A0A0A] hover:bg-white/[0.02] transition-colors"
                  >
                    <span className="text-white font-medium pr-4">{question}</span>
                    <ChevronDown
                      className={"w-5 h-5 text-[#00C896] shrink-0 transition-transform duration-200 " + (
                        openFaq === index ? "rotate-180" : ""
                      )}
                    />
                  </button>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="px-5 pb-5 bg-[#0A0A0A]"
                    >
                      <p className="text-slate-400 leading-relaxed">{answer}</p>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function RelatedServicesSection({ services }) {
  if (!services || services.length === 0) return null;

  return (
    <section className="border-t border-white/5 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            You May Also Need
          </span>
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Related Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {services.map((service) => {
              const serviceId = service.id || "";
              const serviceLabel = service.label || "";
              return (
                <Link
                  key={serviceId}
                  to={"/services/" + serviceId}
                  className="p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00C896]/30 hover:bg-[#00C896]/5 transition-all text-center group"
                >
                  <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                    {serviceLabel}
                  </span>
                </Link>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Main component
export default function ServiceDetailPage() {
  const { serviceSlug } = useParams();
  const [openFaq, setOpenFaq] = useState(null);
  const [crmPricingMap, setCrmPricingMap] = useState({});

  // Get content using simple bracket notation
  const content = serviceContentData[serviceSlug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);

  useEffect(() => {
    let ignore = false;

    async function loadCrmPricing() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/pricing`);
        if (!response.ok) return;

        const items = await response.json();
        if (!Array.isArray(items) || ignore) return;

        const nextMap = {};
        items.forEach((item) => {
          const key = String(item?.service_id || "").trim();
          if (key) nextMap[key] = item;
        });

        if (!ignore) {
          setCrmPricingMap(nextMap);
        }
      } catch (err) {
        // Keep static fallback prices if CRM pricing API is unavailable.
      }
    }

    loadCrmPricing();
    return () => {
      ignore = true;
    };
  }, []);

  const pricing = useMemo(() => {
    const basePricing = content?.pricing || null;
    if (!basePricing || !Array.isArray(basePricing.packages) || !serviceSlug) {
      return basePricing;
    }

    const mergedPackages = basePricing.packages.map((pkg) => {
      const serviceId = `${serviceSlug}--${toSlug(pkg?.name || "")}`;
      const crmEntry = crmPricingMap[serviceId];

      if (!crmEntry) return pkg;

      return {
        ...pkg,
        price: formatCrmPrice(crmEntry.price, crmEntry.currency),
      };
    });

    return {
      ...basePricing,
      packages: mergedPackages,
    };
  }, [content?.pricing, crmPricingMap, serviceSlug]);

  // 404 state
  if (!content) {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service Not Found</h1>
          <p className="text-slate-400 mb-8">
            The service you are looking for does not exist or content is being updated.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#00C896] font-medium"
          >
            View All Services
            <ArrowRight size={16} />
          </Link>
        </div>
      </main>
    );
  }

  // Destructure content to avoid nested member expressions
  const heroSubtitle = content.heroSubtitle || "";
  const heroTitle = content.heroTitle || "";
  const summary = content.summary || "";
  const trustSignals = content.trustSignals || {};
  const whoNeeds = content.whoNeeds || null;
  const whyRequired = content.whyRequired || null;
  const benefits = content.benefits || [];
  const documentsRequired = content.documentsRequired || null;
  const process = content.process || [];
  const timeline = content.timeline || null;
  const faqs = content.faqs || [];
  const relatedServices = content.relatedServices || [];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <main data-testid={"service-page-" + serviceSlug} className="pt-24 md:pt-32 bg-[#050505]">
      {/* Hero Section with Inquiry Form */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left Content - 3 columns */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
              {heroSubtitle}
            </span>
            <h1
              className="text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed mb-8">
              {summary}
            </p>

            <TrustSignals signals={trustSignals} />

            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+919876543210"
                className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-8 py-3.5 rounded-full hover:bg-[#00E6AC] transition-colors"
              >
                <Phone size={18} />
                Call Now
              </a>
              <a
                href="https://wa.me/919876543210"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-medium px-8 py-3.5 rounded-full hover:bg-white/5 transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>

          {/* Right Form - 2 columns */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="lg:sticky lg:top-28">
              <ServiceInquiryForm
                serviceName={heroTitle}
                serviceId={serviceSlug}
                compact={false}
              />
            </div>
          </motion.div>
        </div>
      </section>

      <PricingSection data={pricing} serviceName={heroTitle} serviceSlug={serviceSlug} />
      <DocumentsSection data={documentsRequired} />
      <WhoNeedsSection data={whoNeeds} />
      <WhyRequiredSection data={whyRequired} />
      <BenefitsSection benefits={benefits} />
      <ProcessSection steps={process} />
      <TimelineSection data={timeline} />
      <FAQSection faqs={faqs} openFaq={openFaq} toggleFaq={toggleFaq} />
      <RelatedServicesSection services={relatedServices} />

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Ready to Get Started?
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Our expert team is ready to assist you. Get personalized guidance and complete your requirements efficiently.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-10 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)]"
              >
                Start Now
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:+919899462024"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-medium px-10 py-4 rounded-full hover:bg-white/5 transition-colors"
              >
                Call: +91 9899462024
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
